import React, { useState, useEffect } from "react";
import {
    Button,
    Drawer,
    Form,
    Input,
    Select,
    message,
    Space,
    Tag,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { mapSmsAPIWithErrorHandling } from "../../../api/sms/sms";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const { Option } = Select;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["zns_event_id", "oa", "created_at"];

export default function MapSmsTable() {
    const [form] = Form.useForm();

    // State management
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0,
    });
    const [searchText, setSearchText] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    // Trong component của bạn
    const [availableParams, setAvailableParams] = useState([]);

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    useEffect(() => {
        const fetchParams = async () => {
            try {
                const response = await mapSmsAPIWithErrorHandling.getAllParamsSms();
                if (response.data.success) {
                    setAvailableParams(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching params:', error);
                setAvailableParams([]);
            }
        };

        if (modalVisible) {
            fetchParams();
        }
    }, [modalVisible]);

    // Hàm chèn param vào textarea
    const insertParam = (paramValue) => {
        const currentValue = form.getFieldValue('message') || '';
        const textarea = document.querySelector('textarea#message');
        const cursorPos = textarea?.selectionStart || currentValue.length;

        const newValue =
            currentValue.slice(0, cursorPos) +
            paramValue +
            currentValue.slice(cursorPos);

        form.setFieldsValue({ message: newValue });

        // Focus và đặt con trỏ sau param vừa chèn
        setTimeout(() => {
            textarea?.focus();
            const newPos = cursorPos + paramValue.length;
            textarea?.setSelectionRange(newPos, newPos);
        }, 0);
    };

    // Load data on component mount
    useEffect(() => {
        loadData();
        loadEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load SMS mappings
    const loadData = async (params = {}) => {
        try {
            setLoading(true);
            const response = await mapSmsAPIWithErrorHandling.getMapSmss({
                page: pagination.current,
                per_page: pagination.pageSize,
                search: searchText,
                ...params,
            });

            if (response.data) {
                const { data: responseData } = response.data;
                setData(responseData.data);
                setPagination({
                    ...pagination,
                    current: responseData.current_page,
                    total: responseData.total,
                });
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Load events for select options
    const loadEvents = async () => {
        try {
            const response = await mapZnsAPIWithErrorHandling.getEventZnsAll();
            if (response.data.success) {
                setEvents(response.data.data);
            }
        } catch (error) {
            message.error(error.message);
        }
    };

    // Handle search
    const handleSearch = () => {
        setPagination({ ...pagination, current: 1 });
        loadData({ page: 1, search: searchText });
    };

    // Xoá toàn bộ bộ lọc
    const handleReset = () => {
        setSearchText("");
        setPagination({ ...pagination, current: 1 });
        loadData({ page: 1, search: "" });
    };

    // Handle pagination change
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
        loadData({
            page: newPagination.current,
            per_page: newPagination.pageSize,
        });
    };

    // Show modal for add/edit
    const showModal = (record = null) => {
        setEditingRecord(record);
        setModalVisible(true);

        if (record) {
            form.setFieldsValue({
                zns_event_id: record.zns_event_id,
                oa: record.oa,
                message: record.message,
            });
        }
    };

    // Handle modal cancel
    const handleCancel = () => {
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
    };

    // Handle form submit
    const handleSubmit = async (values) => {
        try {
            setModalLoading(true);

            if (editingRecord) {
                // Update existing record
                await mapSmsAPIWithErrorHandling.updateMapSms(editingRecord.id, values);
                message.success("Cập nhật liên kết SMS thành công");
            } else {
                // Create new record
                await mapSmsAPIWithErrorHandling.createMapSms(values);
                message.success("Tạo liên kết SMS thành công");
            }

            handleCancel();
            loadData();
        } catch (error) {
            message.error(error.message);
        } finally {
            setModalLoading(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        const confirmed = await confirmDelete({ entity: "liên kết SMS" });
        if (!confirmed) return;

        try {
            await mapSmsAPIWithErrorHandling.deleteMapSms(id);
            message.success("Xóa liên kết SMS thành công");
            loadData();
        } catch (error) {
            message.error(error.message);
        }
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT",
                key: "index",
                width: 60,
                render: (_, record, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "zns_event_id", label: "Sự kiện",
            column: {
                title: "Sự kiện",
                dataIndex: "zns_event_id",
                key: "zns_event_id",
                render: (eventId) => {
                    const event = events.find((e) => e.id === eventId);
                    return event ? (
                        <Tag color="blue">{event.name}</Tag>
                    ) : (
                        <Tag color="red">Không xác định</Tag>
                    );
                },
            },
        },
        {
            key: "oa", label: "OA",
            column: {
                title: "OA",
                dataIndex: "oa",
                key: "oa",
            },
        },
        {
            key: "created_at", label: "Ngày tạo",
            column: {
                title: "Ngày tạo",
                dataIndex: "created_at",
                key: "created_at",
                render: (text) => new Date(text).toLocaleString("vi-VN"),
            },
        },
        actionColumnDef(
            (record) => [
                { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
                { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record.id) },
            ],
            { width: 100 }
        ),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Input.Search
                    placeholder="Tìm kiếm..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                    allowClear
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!searchText;

    return (
        <BrandedPageLayout
            title="Cấu hình SMS"
            subtitle="Quản lý liên kết mẫu tin SMS theo sự kiện."
            breadcrumbItems={[{ title: "Cấu hình SMS" }, { title: "Danh sách" }]}
        >
            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleReset}
                onReload={() => loadData({ page: pagination.current, per_page: pagination.pageSize })}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal()}
                    >
                        Thêm liên kết SMS
                    </Button>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />

            {/* Add/Edit Drawer */}
            <Drawer
                title={editingRecord ? "Chỉnh sửa liên kết SMS" : "Thêm liên kết SMS"}
                placement="right"
                width="min(560px, 100vw)"
                open={modalVisible}
                onClose={handleCancel}
                destroyOnClose
                styles={{ body: { background: neutral[50], padding: spacing[6] } }}
                footer={(
                    <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button onClick={handleCancel}>Hủy</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            form="smsForm" // liên kết với form
                        >
                            {editingRecord ? "Cập nhật" : "Tạo mới"}
                        </Button>
                    </Space>
                )}
            >
                <Form
                    id="smsForm"
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="zns_event_id"
                        label="Sự kiện"
                        rules={[
                            { required: true, message: "Vui lòng chọn sự kiện" },
                        ]}
                    >
                        <Select placeholder="Chọn sự kiện">
                            {events.map((event) => (
                                <Option key={event.id} value={event.id}>
                                    {event.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="oa"
                        label="OA (Official Account)"
                        rules={[
                            { required: true, message: "Vui lòng nhập tên OA" },
                        ]}
                    >
                        <Input placeholder="Nhập tên OA" />
                    </Form.Item>

                    <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>
                            Chọn tham số để chèn:
                        </div>
                        <Space wrap size={[8, 8]}>
                            {availableParams.map((param) => (
                                <Tag
                                    key={param.value}
                                    color="blue"
                                    style={{ cursor: 'pointer', margin: 0 }}
                                    onClick={() => insertParam(param.value)}
                                >
                                    {param.label}
                                </Tag>
                            ))}
                        </Space>
                    </div>

                    <Form.Item
                        name="message"
                        label="Nội dung tin nhắn"
                        rules={[
                            { required: true, message: "Vui lòng nhập nội dung tin nhắn" },
                        ]}
                    >
                        <Input.TextArea
                            id="message"
                            placeholder={`Ví dụ:\n\nXin chào {tenkh},\nSố điện thoại: {sdt}\nEmail: {email}\n\nCảm ơn,`}
                            rows={8}
                            showCount
                            maxLength={2000}
                        />
                    </Form.Item>
                </Form>
            </Drawer>
        </BrandedPageLayout>
    );
}
